import { alpha, darken, keyframes, lighten } from '@mui/material/styles'
import { VIEWER_UPPER_CONTROL_HEIGHT } from '@/components/Viewer/ViewerUpper/viewerUpperToolbarStyles'

export type StepAssistantDisplayVariant = 'floating' | 'docked'

export type StepAssistantAppearance = 'light' | 'dark'

export type StepAssistantColorScheme = {
  background: string
  surface: string
  surfaceElevated: string
  border: string
  borderSubtle: string
  foreground: string
  muted: string
  accent: string
  accentDark: string
  accentGlow: string
  actionBackground: string
  inputBackground: string
}

export const stepAssistantFloatingColors: StepAssistantColorScheme = {
  background: '#0F1318',
  surface: '#151A21',
  surfaceElevated: '#1A2029',
  border: '#2A3140',
  borderSubtle: '#232933',
  foreground: '#F8FAFC',
  muted: '#94A3B8',
  accent: '#2DD4BF',
  accentDark: '#0F766E',
  accentGlow: 'rgba(45, 212, 191, 0.28)',
  actionBackground: '#171C24',
  inputBackground: '#12161D',
}

export const stepAssistantDockedColors: StepAssistantColorScheme = {
  background: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceElevated: '#F9FAFB',
  border: '#E4E7EC',
  borderSubtle: '#EAECF0',
  foreground: '#101828',
  muted: '#667085',
  accent: stepAssistantFloatingColors.accent,
  accentDark: stepAssistantFloatingColors.accentDark,
  accentGlow: stepAssistantFloatingColors.accentGlow,
  actionBackground: '#F9FAFB',
  inputBackground: '#FFFFFF',
}

/** @deprecated Préférer getStepAssistantColors(appearance) ou useStepAssistantColors() */
export const stepAssistantColors = stepAssistantFloatingColors

export const getStepAssistantColors = (
  appearance: StepAssistantAppearance
): StepAssistantColorScheme =>
  appearance === 'light'
    ? stepAssistantDockedColors
    : stepAssistantFloatingColors

/** Accent d'une autre couleur (hex) : dégradé et halo en sont dérivés. */
export const withStepAssistantAccent = (
  colors: StepAssistantColorScheme,
  accent?: string
): StepAssistantColorScheme =>
  accent
    ? {
        ...colors,
        accent,
        accentDark: darken(accent, 0.35),
        accentGlow: alpha(accent, 0.28),
      }
    : colors

/**
 * `themed` (apparence claire) : couleurs du thème de l'app, clair ou sombre ; sinon la palette
 * de l'assistant, dont les valeurs claires restaient blanches en mode sombre.
 */
export const getStepAssistantIconButtonSx = (
  colors: StepAssistantColorScheme,
  themed = false
) =>
  ({
    width: VIEWER_UPPER_CONTROL_HEIGHT,
    height: VIEWER_UPPER_CONTROL_HEIGHT,
    minWidth: VIEWER_UPPER_CONTROL_HEIGHT,
    minHeight: VIEWER_UPPER_CONTROL_HEIGHT,
    p: 0,
    flexShrink: 0,
    borderRadius: 1,
    border: '1px solid',
    borderColor: themed ? 'divider' : colors.border,
    bgcolor: themed ? 'background.paper' : colors.surfaceElevated,
    color: themed ? 'text.secondary' : colors.muted,
    boxSizing: 'border-box',
    '&:hover': {
      bgcolor: themed ? 'action.hover' : colors.actionBackground,
      borderColor: themed ? 'divider' : colors.border,
      color: themed ? 'text.primary' : colors.foreground,
    },
  } as const)

export const stepAssistantIconButtonSx = getStepAssistantIconButtonSx(
  stepAssistantFloatingColors
)

export const stepAssistantTypography = {
  heading: 18,
  body: 12,
  caption: 10,
  bodyLineHeight: 1.5,
  headingLineHeight: 1.25,
} as const

/** Lueur d'aurore qui dérive lentement, partagée par l'en-tête et le bouton Assistant. */
export const stepAssistantAuroraDrift = keyframes`
  0%, 100% { background-position: 85% 0%, 0 0; }
  50% { background-position: 60% 100%, 0 0; }
`

/**
 * Dégradé « aurore » de l'identité Assistant Nabu : une lueur d'accent dans le coin sur un fond
 * qui va du foncé à l'accent. `deep` (en-tête) presque noir, `vivid` (bouton plein) reste violet.
 */
export const getStepAssistantAuroraSx = (
  accent: string,
  intensity: 'deep' | 'vivid'
) => {
  const deep = intensity === 'deep'
  return {
    backgroundImage: `radial-gradient(120% 180% at 85% 0%, ${
      deep ? alpha(accent, 0.55) : alpha(lighten(accent, 0.35), 0.9)
    } 0%, transparent 55%), linear-gradient(120deg, ${darken(
      accent,
      deep ? 0.82 : 0.45
    )} 0%, ${deep ? darken(accent, 0.62) : accent} 100%)`,
    backgroundSize: '160% 160%, 100% 100%',
    animation: `${stepAssistantAuroraDrift} 14s ease-in-out infinite`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  } as const
}
